import React from 'react';
import { HelpCircle } from 'lucide-react';
import SEO from '../components/SEO';
import './About.css';

const About = () => {
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is Cosmergence a religion?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A philosophical worldview with a spiritual tone. No deity required; curiosity and compassion are enough."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need rituals for Cosmergence?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Optional. Practice daily tuning, acts of resonance, and moments of awe—or simply live with awareness of the cosmic flow."
        }
      },
      {
        "@type": "Question",
        "name": "Can I be Cosmergent and practice another faith?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. Cosmergence is compatible with any worldview that honors continuity, compassion, and cosmic wonder."
        }
      }
    ]
  };

  const faqs = [
    {
      question: 'Is this a religion?',
      answer: 'A philosophical worldview with a spiritual tone. No deity required; curiosity and compassion are enough.'
    },
    {
      question: 'Do I need rituals?',
      answer: 'Optional. Practice daily tuning, acts of resonance, and moments of awe—or simply live with awareness of the cosmic flow.'
    },
    {
      question: 'What about the symbols?',
      answer: 'Continuum Circle, Cosmic River, Quantum Note—reminders, not requirements. Use them if they resonate, or find your own.'
    },
    {
      question: 'Can I be Cosmergent and practice another faith?',
      answer: 'Absolutely. Cosmergence is compatible with any worldview that honors continuity, compassion, and cosmic wonder.'
    }
  ];

  return (
    <div className="about-page">
      <SEO
        title="About Cosmergence"
        description="Is Cosmergence a religion? Learn about our philosophy, origin, and frequently asked questions. A science-based spiritual worldview compatible with any faith or no faith."
        keywords="is cosmergence a religion, spiritual but not religious, philosophy vs religion, secular spirituality, science and spirituality, alternative to religion"
        canonicalUrl="/about"
        structuredData={faqStructuredData}
      />
      
      {/* Hero Section */}
      <section className="about-hero cosmic-bg">
        <div className="container">
          <h1 className="heading-1 text-center">About Cosmergence</h1>
          <p className="body-large text-center" style={{maxWidth: '700px', margin: '24px auto 0'}}>
            A philosophy born from the desire to reconcile life and death with science and wonder.
          </p>
        </div>
      </section>

      {/* Origin Section */}
      <section className="section-padding">
        <div className="container">
          <div className="origin-section">
            <h2 className="heading-2 mb-4">Origin</h2>
            <p className="body-large" style={{marginBottom: '24px'}}>
              Cosmergence grew from a simple desire: reconcile life and death with science and wonder. We see ourselves as songs of the cosmos—temporary, beautiful, and continuous in effect.
            </p>
            <p className="body-large">
              Drawing on principles from physics, cosmology, and the universal human need for meaning, Cosmergence offers a framework for understanding existence without requiring supernatural beliefs.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding-small cosmic-bg">
        <div className="container">
          <h2 className="heading-2 text-center mb-8">Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item card">
                <div className="faq-question">
                  <HelpCircle size={24} className="text-gold" />
                  <h3 className="heading-3">{faq.question}</h3>
                </div>
                <p className="body-regular faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding">
        <div className="container">
          <div className="mission-section">
            <h2 className="heading-2 text-center mb-4">Our Mission</h2>
            <p className="body-large text-center" style={{maxWidth: '800px', margin: '0 auto 24px'}}>
              To offer a compassionate, scientifically-grounded perspective on existence that honors both the temporary nature of individual consciousness and the eternal continuity of energy and matter.
            </p>
            <p className="body-large text-center" style={{maxWidth: '800px', margin: '0 auto'}}>
              We believe that understanding ourselves as cosmic patterns can bring comfort, meaning, and ethical clarity to human life.
            </p>
          </div>
        </div>
      </section>

      {/* Symbols Section */}
      <section className="section-padding-small">
        <div className="container">
          <div className="symbols-section">
            <h2 className="heading-2 text-center mb-8">Our Symbols</h2>
            <div className="symbols-grid">
              <div className="symbol-card card">
                <h3 className="heading-3 text-gold">Continuum Circle</h3>
                <p className="body-regular">
                  Represents the unbroken cycle of matter and energy, with no beginning or end.
                </p>
              </div>
              <div className="symbol-card card">
                <h3 className="heading-3 text-gold">Cosmic River</h3>
                <p className="body-regular">
                  The flow of energy through space and time, carrying us all.
                </p>
              </div>
              <div className="symbol-card card">
                <h3 className="heading-3 text-gold">Quantum Note</h3>
                <p className="body-regular">
                  Consciousness as a musical pattern, a temporary harmony in the cosmic song.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;